// The page side of the preview frame. Every 3D preview that runs asset code (the asset page, the avatar creator, the
// review queue's baked model) lives in a sandboxed iframe with an opaque origin: sandbox="allow-scripts" and never
// allow-same-origin, served with a strict CSP (serve.ts). The frame can't read this page's storage or DOM and never gets
// a session token; it gets params and package file paths by postMessage, and only expected shapes come back.

import { h } from "./dom.ts";
import { FRAME_PATH, parseFromFrame, type AssetStage, type AvatarClip, type AvatarSpecMsg, type FromFrame, type SceneId, type ToFrame } from "./frame/protocol.ts";
import type { Manifest, Stats, Value } from "./store.ts";

/** Where other packages (the standard body, wearables) come from: the site's read-only mount of the starter set. */
export const PACKAGES = "/store/packages/";

/** Mount a preview frame in `stage`; messages to it wait until it says it's ready. */
export function previewFrame(stage: HTMLElement, label: string, onMessage: (m: FromFrame) => void): { send(m: ToFrame): void } {
  const frame = h("iframe", { src: FRAME_PATH, sandbox: "allow-scripts", class: "preview-frame", title: label, referrerpolicy: "no-referrer", loading: "eager" });
  stage.prepend(frame);
  let ready = false;
  const queue: ToFrame[] = [];
  const post = (m: ToFrame) => frame.contentWindow?.postMessage(m, "*"); // an opaque origin can only be addressed as "*"; nothing sent is secret
  const listener = (e: MessageEvent) => {
    if (!frame.isConnected) return window.removeEventListener("message", listener);
    if (e.source !== frame.contentWindow || e.origin !== "null") return;
    const m = parseFromFrame(e.data);
    if (!m) return;
    if (m.t === "ready") {
      ready = true;
      for (const q of queue.splice(0)) post(q);
    }
    onMessage(m);
  };
  window.addEventListener("message", listener);
  return {
    send(m) {
      if (ready) post(m);
      else queue.push(m);
    },
  };
}

export interface PreviewOpts {
  /** package files path, ending in "/" (the store's files route or the mount) */
  base: string;
  manifest: Manifest;
  /**
   * "live": run the asset with params; "baked": the default bake only, no asset code; "preview": the store's public
   * low-detail model (paid assets; `base` is the site's /api/store-preview/<id>/<version>/), no asset code either
   */
  mode: "live" | "baked" | "preview";
  /** "baked" mode: the model's path when it isn't at base + bake.default (a signed download) */
  bakedUrl?: string;
  onStats?: (s: Stats) => void;
  onNote?: (text: string) => void;
  /** the backdrop: "dusk" (default) or "workshop" (the Assets zone) */
  stage?: AssetStage;
  /** fires once, when the first model is on stage */
  onLoaded?: () => void;
  /** decoration behind a page's own copy (a zone hero): out of the tab order and the accessibility tree */
  decorative?: boolean;
}

export function startAssetPreview(stage: HTMLElement, o: PreviewOpts): { show(params: Record<string, Value>): void } {
  let loaded = false;
  const f = previewFrame(stage, o.decorative ? "" : `3D preview of ${o.manifest.title ?? o.manifest.id}`, (m) => {
    if (m.t === "note") {
      o.onNote?.(m.text);
      // the scene says "Drag to turn." once a model is up (and "unavailable" when it can't be)
      if (!loaded && /drag to turn/i.test(m.text)) {
        loaded = true;
        o.onLoaded?.();
      }
    } else if (m.t === "stats") o.onStats?.(m.stats);
  });
  if (o.decorative) {
    const el = stage.firstElementChild;
    el?.setAttribute("tabindex", "-1");
    el?.setAttribute("aria-hidden", "true");
  }
  const init: ToFrame = { t: "init", kind: "asset", mode: o.mode, base: o.base, manifest: o.manifest, bakedUrl: o.bakedUrl, bodyBase: PACKAGES };
  if (o.stage) init.stage = o.stage;
  f.send(init);
  return { show: (params) => f.send({ t: "params", params }) };
}

export interface AvatarPreview {
  show(spec: AvatarSpecMsg): void;
  focus(view: "full" | "face"): void;
  play(clip: AvatarClip): void;
  /** the portrait of what's on stage now, as JPEG bytes (null when the preview can't make one) */
  portrait(): Promise<Blob | null>;
}

export function startAvatarPreview(stage: HTMLElement): AvatarPreview {
  const note = (text: string) => {
    const p = stage.querySelector(".stage-note");
    if (p) p.textContent = text;
  };
  let waiting: ((jpeg: string | null) => void)[] = [];
  const f = previewFrame(stage, "Avatar preview", (m) => {
    if (m.t === "note") note(m.text);
    else if (m.t === "portrait") for (const w of waiting.splice(0)) w(m.jpeg);
  });
  f.send({ t: "init", kind: "avatar", packages: PACKAGES });
  return {
    show: (spec) => f.send({ t: "avatar", spec: structuredClone(spec) }),
    focus: (view) => f.send({ t: "focus", view }),
    play: (clip) => f.send({ t: "play", clip }),
    async portrait() {
      const jpeg = await new Promise<string | null>((done) => {
        waiting.push(done);
        f.send({ t: "portrait" });
        setTimeout(() => { waiting = waiting.filter((w) => w !== done); done(null); }, 12_000);
      });
      if (!jpeg) return null;
      const bin = atob(jpeg.slice(jpeg.indexOf(",") + 1));
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new Blob([bytes], { type: "image/jpeg" });
    },
  };
}

/** A zone's hero scene (src/frame/scenes/), behind the zone's copy: `onLoaded` fires after its first frame. */
export function startScenePreview(stage: HTMLElement, scene: SceneId, quality: "high" | "low", onLoaded: () => void): void {
  const f = previewFrame(stage, "", (m) => {
    if (m.t === "loaded") onLoaded();
    else if (m.t === "note") console.warn("scene:", m.text);
  });
  // decoration behind the page's own copy: out of the tab order and the accessibility tree
  const el = stage.firstElementChild;
  el?.setAttribute("tabindex", "-1");
  el?.setAttribute("aria-hidden", "true");
  f.send({ t: "init", kind: "scene", scene, packages: PACKAGES, quality });
}
